import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import styled, {
  ThemeProvider,
  createGlobalStyle,
  keyframes,
  css,
} from 'styled-components';
import {
  clearMsg,
  forgotPasword,
  resetPassword,
  varifyPasswordotp,
} from '../../../redux/actions/authActions';
import {
  getUserInfo1,
  verifyTFAEmail,
} from '../../../utils/services/user.service';
// Header removed - home module deleted

const theme = {
  bg: '#0b0e14',
  card: '#111522',
  card2: '#0d1220',
  text: '#e5e7eb',
  muted: '#94a3b8',
  border: '#1f2937',
  accent: '#00f7d2',
  accent2: '#5ce9ff',
  success: '#10b981',
  warning: '#fbbf24',
  weak: '#ef4444',
  error: '#f87171',
};

const EMAIL_OTP_LENGTH = 4;
const TFA_OTP_LENGTH = 4;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    background: ${(props) => props.theme.bg};
    color: ${(props) => props.theme.text};
    font-family: system-ui, Segoe UI, Roboto, Arial;
  }
`;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 20px;
  background:
    radial-gradient(900px 900px at 80% -10%, rgba(0, 247, 210, 0.08), transparent),
    radial-gradient(1000px 1000px at -10% 110%, rgba(92, 233, 255, 0.07), transparent),
    ${(props) => props.theme.bg};
  padding: 24px 12px;
`;

const StepBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: min(420px, 90%);
  margin-bottom: 8px;
`;

const Step = styled.div`
  flex: 1;
  height: 4px;
  margin: 0 4px;
  border-radius: 4px;
  background: ${(props) => (props.active ? props.theme.accent : props.theme.border)};
  transition: background 0.3s ease;
`;

const Card = styled.div`
  background: ${(props) => props.theme.card};
  border: 1px solid ${(props) => props.theme.border};
  border-radius: 18px;
  padding: 50px 40px;
  width: min(420px, 90%);
  box-shadow: 0 10px 35px rgba(0, 0, 0, 0.6);
  text-align: center;
  animation: ${fadeIn} 0.4s ease both;

  @media (max-width: 480px) {
    padding: 36px 24px;
  }
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(90deg, ${(props) => props.theme.accent}, ${(props) => props.theme.accent2});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 6px;
`;

const Subtitle = styled.p`
  color: ${(props) => props.theme.muted};
  font-size: 0.95rem;
  margin-bottom: 28px;
`;

const Label = styled.label`
  font-size: 0.85rem;
  color: ${(props) => props.theme.muted};
  display: block;
  margin-bottom: 6px;
  text-align: left;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid ${(props) => props.theme.border};
  background: ${(props) => props.theme.card2};
  color: ${(props) => props.theme.text};
  outline: none;
  margin-bottom: 6px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const StrengthWrapper = styled.div`
  margin-top: 6px;
  margin-bottom: 10px;
`;

const StrengthBar = styled.div`
  display: flex;
  gap: 4px;
  height: 6px;
  margin-bottom: 6px;
`;

const Segment = styled.div`
  flex: 1;
  border-radius: 3px;
  background: ${(props) => (props.active ? props.color : props.theme.border)};
  transition: all 0.4s ease;

  ${(props) =>
    props.active &&
    css`
      background: linear-gradient(270deg, ${props.color}, ${props.theme.accent2});
      background-size: 200% 200%;
      animation: ${pulse} 3s ease infinite;
    `}
`;

const StrengthText = styled.div`
  font-size: 0.8rem;
  color: ${(props) => props.color};
  text-align: left;
`;

const Feedback = styled.div`
  font-size: 0.8rem;
  color: ${(props) => (props.type === 'error' ? props.theme.error : props.theme.accent2)};
  text-align: left;
  margin-bottom: 12px;
  min-height: 16px;
`;

const OtpGrid = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 18px;
`;

const OtpBox = styled.input`
  width: 40px;
  height: 48px;
  text-align: center;
  border-radius: 10px;
  border: 2px solid ${(props) => (props.filled ? props.theme.success : props.theme.border)};
  background: ${(props) => props.theme.card2};
  color: ${(props) => props.theme.text};
  font-size: 1.2rem;
  outline: none;
  transition: 0.2s ease;

  &:focus {
    border-color: ${(props) => props.theme.accent2};
    box-shadow: 0 0 6px ${(props) => props.theme.accent2};
  }
`;

const Button = styled.button`
  width: 100%;
  border: none;
  border-radius: 12px;
  background: linear-gradient(120deg, ${(props) => props.theme.accent}, ${(props) => props.theme.accent2});
  color: #001018;
  font-weight: 700;
  font-size: 0.9rem;
  padding: 12px;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const Resend = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.accent2};
  font-size: 0.8rem;
  cursor: pointer;
  margin-top: 8px;
  text-decoration: underline;
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};

  &:disabled {
    cursor: not-allowed;
  }
`;

const ProgressWrapper = styled.div`
  height: 6px;
  background: ${(props) => props.theme.border};
  border-radius: 10px;
  overflow: hidden;
  margin-top: 12px;
  margin-bottom: 6px;
`;

const ProgressBar = styled.div`
  height: 100%;
  background: ${(props) => props.color || props.theme.accent};
  width: ${(props) => props.width}%;
  transition: width 0.5s ease-in-out;
`;

const HelperRow = styled.div`
  font-size: 0.8rem;
  margin-top: 8px;
  color: ${(props) => props.theme.muted};

  span {
    color: ${(props) => props.theme.accent2};
  }
`;

const CaptchaWrapper = styled.div`
  margin-bottom: 18px;
  display: flex;
  justify-content: center;
`;

const SuccessText = styled.p`
  color: ${(props) => props.theme.accent};
  font-size: 0.95rem;
  margin-bottom: 24px;
`;

const footerStages = ['reset', 'otp', 'newpass', 'otp2', 'confirmation'];

const ResetPassword = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { message: globalMessage } = useSelector((state) => state.message);

  const [stage, setStage] = useState('reset');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(Array(EMAIL_OTP_LENGTH).fill(''));
  const [otp2, setOtp2] = useState(Array(TFA_OTP_LENGTH).fill(''));
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [timer2, setTimer2] = useState(60);
  const [verifiedOtp, setVerifiedOtp] = useState('');
  const [tfaRequired, setTfaRequired] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const otpRefs = useRef([]);
  const otp2Refs = useRef([]);

  useEffect(() => {
    if (globalMessage) {
      setStatusMessage(globalMessage);
    }
  }, [globalMessage]);

  useEffect(() => () => {
        dispatch(clearMsg());
  }, [dispatch]);

  useEffect(() => {
    if (stage === 'otp') {
      setTimer(60);
    }
    if (stage === 'otp2') {
      setTimer2(60);
    }
  }, [stage]);

  useEffect(() => {
    if ((stage === 'reset' || stage === 'otp') && timer > 0) {
      const interval = setInterval(
        () => setTimer((prev) => Math.max(prev - 1, 0)),
        1000,
      );
      return () => clearInterval(interval);
    }
    return undefined;
  }, [timer, stage]);

  useEffect(() => {
    if (stage === 'otp2' && timer2 > 0) {
      const interval = setInterval(
        () => setTimer2((prev) => Math.max(prev - 1, 0)),
        1000,
      );
      return () => clearInterval(interval);
    }
    return undefined;
  }, [timer2, stage]);

  const strength = useMemo(() => getStrength(password, theme), [password]);
  const otpValue = useMemo(() => otp.join(''), [otp]);
  const otp2Value = useMemo(() => otp2.join(''), [otp2]);

  const stageOrder = useMemo(
    () => (tfaRequired ? footerStages : footerStages.filter((item) => item !== 'otp2')),
    [tfaRequired],
  );

  const currentIndex = stageOrder.indexOf(stage);

  const progress1 = (timer / 60) * 100;
  const progress2 = (timer2 / 60) * 100;
  const color1 = timer > 30 ? theme.accent2 : timer > 10 ? theme.warning : theme.weak;
  const color2 = timer2 > 30 ? theme.accent2 : timer2 > 10 ? theme.warning : theme.weak;

  const handleOtpChange = (value, index, target = 'otp') => {
    if (/\D/.test(value)) {
      return;
    }
    const next = target === 'otp' ? [...otp] : [...otp2];
    next[index] = value;
    if (target === 'otp') {
      setOtp(next);
    } else {
      setOtp2(next);
    }
    const refs = target === 'otp' ? otpRefs : otp2Refs;
    const maxIndex = (target === 'otp' ? EMAIL_OTP_LENGTH : TFA_OTP_LENGTH) - 1;
    if (value && index < maxIndex) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (event, index, target = 'otp') => {
    if (event.key === 'Backspace' && !event.target.value && index > 0) {
      const refs = target === 'otp' ? otpRefs : otp2Refs;
      refs.current[index - 1]?.focus();
    }
  };

  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    if (!email) {
      setError('Email is required.');
      return;
    }
    if (!recaptchaToken) {
      setError('Complete the CAPTCHA challenge before continuing.');
      return;
    }
    setError('');
    setStatusMessage('');
    setLoading(true);
    dispatch(clearMsg());
    try {
      await dispatch(forgotPasword(email, recaptchaToken));
      setOtp(Array(EMAIL_OTP_LENGTH).fill(''));
      setStage('otp');
      setTimer(60);
      setStatusMessage('We sent a one-time code to your email.');
      setRecaptchaToken(null);
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      if (errorMessage === 'Missing CAPTCHA token') {
        setError('Complete the CAPTCHA challenge before continuing.');
        setRecaptchaToken(null);
        return;
      }
      if (errorMessage === 'CAPTCHA verification failed') {
        setError('CAPTCHA verification failed. Please try again.');
        setRecaptchaToken(null);
        return;
      }
      setError(globalMessage || 'Unable to send OTP. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (event) => {
    event.preventDefault();
    if (otpValue.length !== EMAIL_OTP_LENGTH) {
      setError(`Enter the ${EMAIL_OTP_LENGTH}-digit code sent to your email.`);
      return;
    }
    setLoading(true);
    setError('');
    dispatch(clearMsg());
    try {
      await dispatch(varifyPasswordotp(email, otpValue));
      setVerifiedOtp(otpValue);
      setStage('newpass');
      setStatusMessage('OTP verified. Set your new password.');
    } catch (err) {
      setError(globalMessage || 'Invalid OTP. Check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setError('');
            dispatch(clearMsg());

    if (!password.trim() || !confirm.trim()) {
      setError('All fields are required.');
      return;
    }

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    if (strength.label === 'Weak') {
      setError('Password too weak. Add uppercase letters, numbers, and symbols.');
      return;
    }

    if (!recaptchaToken) {
      setError('Complete the CAPTCHA challenge before continuing.');
      return;
    }

    setLoading(true);
    try {
      await dispatch(resetPassword(email, verifiedOtp || otpValue, password, confirm, recaptchaToken));
      setStatusMessage('Password updated. Final verification in progress.');
      setRecaptchaToken(null);
      await handlePostReset(email);
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      if (errorMessage === 'Missing CAPTCHA token') {
        setError('Complete the CAPTCHA challenge before continuing.');
        setRecaptchaToken(null);
        return;
      }
      if (errorMessage === 'CAPTCHA verification failed') {
        setError('CAPTCHA verification failed. Please try again.');
        setRecaptchaToken(null);
        return;
      }
      setError(globalMessage || 'Unable to reset password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePostReset = async (emailAddress) => {
    try {
      const response = await getUserInfo1({ email: emailAddress });
      const requiresTfa = response?.data?.User?.google_auth_status === true;
      if (requiresTfa) {
        setTfaRequired(true);
        setOtp2(Array(TFA_OTP_LENGTH).fill(''));
        setStage('otp2');
        setStatusMessage('Enter the code from your authenticator app to finish.');
        } else {
        setStage('confirmation');
        setStatusMessage('Your password has been verified and updated.');
      }
    } catch (err) {
      setStage('confirmation');
      setStatusMessage('Password reset successfully.');
    }
  };

  const handleSecondOtpSubmit = async (event) => {
    event.preventDefault();
    if (otp2Value.length !== TFA_OTP_LENGTH) {
      setError(`Enter the ${TFA_OTP_LENGTH}-digit authenticator code.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const requestData = {
        token: otp2Value,
        email,
      };
      const result = await verifyTFAEmail(requestData);
      if (result?.data?.verified === true) {
        setStage('confirmation');
        setStatusMessage('Two-factor verification complete. You can log in.');
      } else {
        setError('Verification failed. Check the code and try again.');
      }
    } catch (err) {
      const apiMessage = err?.response?.data?.Message || 'Verification failed. Try again.';
      setError(apiMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (type = 'otp') => {
    if (loading) {
      return;
    }
    if (type === 'otp') {
      if (timer > 0) {
        return;
      }
      if (!email) {
        setError('Enter your email before requesting a new code.');
        return;
      }
      if (!recaptchaToken) {
        setError('Complete the CAPTCHA challenge before continuing.');
        return;
      }
      setError('');
      setStatusMessage('');
      setTimer(60);
      setOtp(Array(EMAIL_OTP_LENGTH).fill(''));
      try {
        dispatch(clearMsg());
        await dispatch(forgotPasword(email, recaptchaToken));
        setStatusMessage('We sent a new code to your email.');
        setRecaptchaToken(null);
      } catch (err) {
        const errorMessage = err?.response?.data?.message;
        if (errorMessage === 'Missing CAPTCHA token') {
          setError('Complete the CAPTCHA challenge before continuing.');
          setRecaptchaToken(null);
          setTimer(0);
          return;
        }
        if (errorMessage === 'CAPTCHA verification failed') {
          setError('CAPTCHA verification failed. Please try again.');
          setRecaptchaToken(null);
          setTimer(0);
          return;
        }
        setError(globalMessage || 'Unable to resend. Try again later.');
        setTimer(0);
      }
      return;
    }
    if (type === 'otp2') {
      setStatusMessage('Open your authenticator app for a fresh code.');
    }
  };

  const renderResendRow = (type = 'otp') => {
    if (type === 'otp2' && !tfaRequired) {
      return null;
    }
    if (type === 'otp2') {
      return (
        <HelperRow>
          Use your authenticator app. Codes refresh automatically.
        </HelperRow>
      );
    }
    return (
      <HelperRow>
        {timer > 0 ? (
          <>
            Resend available in <span>{timer}s</span>
          </>
        ) : (
          <Resend type="button" onClick={() => handleResend('otp')}>
            Resend Code
          </Resend>
        )}
      </HelperRow>
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
        {/* Header removed */}
      <Wrapper>
        <StepBar>
          {stageOrder.map((item, index) => (
            <Step
              // eslint-disable-next-line react/no-array-index-key
              key={`${item}-${index}`}
              active={index <= currentIndex}
            />
          ))}
        </StepBar>

        {stage === 'reset' && (
          <Card>
            <Title>Reset Password</Title>
            <Subtitle>Enter your email to receive a one-time code.</Subtitle>
            <form onSubmit={handleEmailSubmit}>
              <Label>Email</Label>
              <Input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="you@example.com"
                autoComplete="email"
              />
              <CaptchaWrapper>
                <ReCAPTCHA
                  sitekey="6LfZ2U8pAAAAAIOF3a4FMW8h7jEbm99sDVQMEKn0"
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                  theme="dark"
                />
              </CaptchaWrapper>
              <Feedback type={error ? 'error' : 'info'}>
                {error || statusMessage}
              </Feedback>
              <Button type="submit" disabled={loading || !recaptchaToken}>
                {loading ? 'Sending…' : 'Send OTP'}
              </Button>
            </form>
  
          </Card>
        )}

        {stage === 'otp' && (
          <Card>
            <Title>Enter OTP Code</Title>
            <Subtitle>
              {EMAIL_OTP_LENGTH}
              -digit code sent to
              {' '}
              <strong>{email}</strong>
            </Subtitle>
            <form onSubmit={handleOtpSubmit}>
              <OtpGrid>
                {otp.map((digit, index) => (
                  <OtpBox
                    key={`otp-${index}`}
                    maxLength={1}
                    value={digit}
                    onChange={(event) => handleOtpChange(event.target.value, index, 'otp')}
                    onKeyDown={(event) => handleOtpKeyDown(event, index, 'otp')}
                    filled={Boolean(digit)}
                    ref={(element) => {
                      otpRefs.current[index] = element;
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                ))}
              </OtpGrid>
              <Feedback type={error ? 'error' : 'info'}>
                {error || statusMessage}
              </Feedback>
              <Button type="submit" disabled={loading}>
                {loading ? 'Verifying…' : 'Verify'}
              </Button>
            </form>
            <ProgressWrapper>
              <ProgressBar width={progress1} color={color1} />
            </ProgressWrapper>
            {renderResendRow('otp')}
          </Card>
        )}

        {stage === 'newpass' && (
          <Card>
            <Title>Set New Password</Title>
            <Subtitle>Choose a secure password before final verification.</Subtitle>
            <form onSubmit={handlePasswordSubmit}>
              <Label>New Password</Label>
              <Input
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="new-password"
              />
              <StrengthWrapper>
                <StrengthBar>
                  {[1, 2, 3, 4].map((item) => (
                    <Segment
                      key={`strength-${item}`}
                      active={item <= strength.score}
                      color={strength.color}
                    />
                  ))}
                </StrengthBar>
                <StrengthText color={strength.color}>
                  {strength.label}
                  {' '}
                  password
                </StrengthText>
              </StrengthWrapper>
              <Label>Confirm Password</Label>
              <Input
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                required
                autoComplete="new-password"
              />
              <CaptchaWrapper>
                <ReCAPTCHA
                  sitekey="6LfZ2U8pAAAAAIOF3a4FMW8h7jEbm99sDVQMEKn0"
                  onChange={(token) => setRecaptchaToken(token)}
                  onExpired={() => setRecaptchaToken(null)}
                  theme="dark"
                />
              </CaptchaWrapper>
              <Feedback type={error ? 'error' : 'info'}>
                {error || statusMessage}
              </Feedback>
              <Button type="submit" disabled={loading || !recaptchaToken}>
                {loading ? 'Saving…' : 'Proceed to Final Step'}
              </Button>
            </form>
          </Card>
        )}

        {stage === 'otp2' && (
          <Card>
            <Title>Final Verification</Title>
            <Subtitle>
              Enter the
              {' '}
              {TFA_OTP_LENGTH}
              -digit authenticator code for
              {' '}
              <strong>{email}</strong>
            </Subtitle>
            <form onSubmit={handleSecondOtpSubmit}>
              <OtpGrid>
                {otp2.map((digit, index) => (
                  <OtpBox
                    key={`otp2-${index}`}
                    maxLength={1}
                    value={digit}
                    onChange={(event) => handleOtpChange(event.target.value, index, 'otp2')}
                    onKeyDown={(event) => handleOtpKeyDown(event, index, 'otp2')}
                    filled={Boolean(digit)}
                    ref={(element) => {
                      otp2Refs.current[index] = element;
                    }}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                ))}
              </OtpGrid>
              <Feedback type={error ? 'error' : 'info'}>
                {error || statusMessage}
              </Feedback>
              <Button type="submit" disabled={loading}>
                {loading ? 'Verifying…' : 'Confirm Reset'}
              </Button>
            </form>
            <ProgressWrapper>
              <ProgressBar width={progress2} color={color2} />
            </ProgressWrapper>
            {renderResendRow('otp2')}
          </Card>
        )}

        {stage === 'confirmation' && (
          <Card>
            <Title>Password Reset Successful</Title>
            <SuccessText>
              {statusMessage || 'Your password has been updated.'}
            </SuccessText>
            <Button type="button" onClick={() => history.push('/sign-in')}>
              Return to Login
            </Button>
          </Card>
        )}
      </Wrapper>
    </ThemeProvider>
  );
};

export default ResetPassword;

function getStrength(password, palette) {
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  if (score <= 1) {
    return { label: 'Weak', color: palette.weak, score };
  }
  if (score === 2 || score === 3) {
    return { label: 'Medium', color: palette.warning, score };
  }
  return { label: 'Strong', color: palette.success, score };
}
